// import { useSetError } from "../redux/modals";
import { Page, Spinner, Dropdown, DropdownToggleType, ButtonType, BPMButton } from "../Components";
import React, { useState, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";

import { MiscLambdaFetch, awaitJSON, pollS3 } from "../utils/fetch-utils";
import * as UserRedux from "../redux/user";

import "./LLM.scss";
import { Modal } from "react-bootstrap";

interface LLMResponseDocument {
  message: string;
  sql: string;
  reason: string;
}

interface LLMResponseContent {
  document: string;
}

interface LLMResponse {
  content: LLMResponseContent;
  completionReason: string;
  executionId?: string;
  nodeName?: string;
  cometErrorMessage?: string;
  completion?: string;
}

interface MessageInfo {
  user?: string;
  message?: string;
  sql?: string;
  reason?: string;
  executionId?: string;
  nodeName?: string;
  isLastForExecutionId?: boolean;
  timestamp?: Date | string;
}

const LOCAL_STORAGE_KEY = "llm";

const LLM = (): JSX.Element => {
  // const setError = useSetError();
  const user = useSelector(UserRedux.fullNameSelector);
  const [messages, setMessages] = useState<MessageInfo[]>(
    JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || "[]") as MessageInfo[]
  );
  const [waitingForResponse, setWaitingForResponse] = useState(
    undefined as MessageInfo | undefined
  );
  const [llm, setLLM] = useState("Flow");
  const userEmail = useSelector(UserRedux.emailSelector);

  const [sqlText, setSqlText] = useState<string>("");
  const [showSqlModal, setShowSqlModal] = useState<boolean>(false);
  const [reasonText, setReasonText] = useState<string>();
  const [showReasonModal, setShowReasonModal] = useState<boolean>(false);

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(messages));
  }, [messages]);

  const handleSqlModal = useCallback(sql => {
    setSqlText(sql);
    setShowSqlModal(true);
  }, []);

  const handleReasonModal = useCallback(reason => {
    setReasonText(reason);
    setShowReasonModal(true);
  }, []);

  const sendPrompt = useCallback(
    async (prompt: string, lastMessage: MessageInfo | undefined) => {
      setWaitingForResponse(lastMessage);
      const result = await MiscLambdaFetch("/kickOffLambda", {
        method: "POST",
        body: {
          fileType: "txt",
          lambdaArgs: {
            prompt,
            executionId: lastMessage?.executionId,
            nodeName: lastMessage?.nodeName,
            configName: llm,
            sessionId: userEmail.split("@")[0],
          },
          lambdaName: "llm-sendPrompt",
        },
      });
      const uuid = await awaitJSON(result);

      const content = await pollS3({
        autoDownload: false,
        bucket: "bpm-cache",
        filename: `${uuid}.txt`,
        mimeType: "text/plain",
      });
      const textContent = await content.text();
      const fullResponse = JSON.parse(textContent) as LLMResponse;
      let responseMessage = "unknown error";
      let responseSql;
      let responseReason;
      if (fullResponse.cometErrorMessage) {
        responseMessage = fullResponse.cometErrorMessage;
      } else if (fullResponse.completionReason === "INPUT_REQUIRED") {
        responseMessage = fullResponse.content.document;
      } else if (fullResponse.completion) {
        responseMessage = fullResponse.completion;
      } else {
        try {
          const parsedResponse = JSON.parse(fullResponse.content.document);
          responseMessage = (parsedResponse as LLMResponseDocument).message;
          responseSql = (parsedResponse as LLMResponseDocument).sql;
          responseReason = (parsedResponse as LLMResponseDocument).reason;
        } catch (e) {
          responseMessage = textContent;
        }
      }

      setWaitingForResponse(undefined);
      setMessages(prevMessages => [
        ...prevMessages,
        { user, message: prompt, timestamp: new Date() },
        {
          user: `LLM (${llm})`,
          message: responseMessage,
          sql: responseSql,
          reason: responseReason,
          executionId:
            fullResponse.completionReason === "INPUT_REQUIRED"
              ? fullResponse.executionId
              : undefined,
          nodeName:
            fullResponse.completionReason === "INPUT_REQUIRED" ? fullResponse.nodeName : undefined,
          timestamp: new Date(),
        },
      ]);
    },
    [user, llm, userEmail]
  );

  const actionsContainer = (
    <div>
      <Dropdown
        type={DropdownToggleType.OUTLINED}
        design="secondary"
        size="sm"
        value={llm}
        options={[{ value: "Flow" }, { value: "Agent" }]}
        onChange={setLLM}
      />
    </div>
  );

  return (
    <Page title={"LLM"} pageType="LLM" actions={actionsContainer}>
      <div className="messages">
        <div className="messages-wrapper">
          {messages.map((elem, index) => {
            return (
              <div className="message-container">
                <div key={index} className="message">
                  <div className={`message-${elem.user === "LLM" ? "llm" : "user"}`}>
                    <b>
                      {elem.user}
                      {elem.timestamp
                        ? ` (${
                            elem.timestamp instanceof Date
                              ? elem.timestamp.toLocaleString("en-US", { timeZoneName: "short" })
                              : elem.timestamp
                          })`
                        : ""}
                    </b>
                    : {elem.message}{" "}
                  </div>
                </div>
                {elem.sql && (
                  <BPMButton
                    className="llm-modal-button"
                    type={ButtonType.FILLED}
                    onClick={() => handleSqlModal(elem.sql)}
                  >
                    See SQL
                  </BPMButton>
                )}
                {elem.reason && (
                  <BPMButton
                    className="llm-modal-button"
                    type={ButtonType.FILLED}
                    onClick={() => handleReasonModal(elem.reason)}
                  >
                    See Reason
                  </BPMButton>
                )}
                {messages[messages.length - 1] === elem && elem.executionId && (
                  <div className="reply-prompt-input-container">
                    {waitingForResponse ? (
                      <Spinner />
                    ) : (
                      <input
                        type="text"
                        className="input-field"
                        placeholder="Respond to existing ..."
                        onKeyDown={async e => {
                          if (e.key === "Enter") {
                            const newMessage = (e.target as HTMLInputElement).value;
                            (e.target as HTMLInputElement).value = "";
                            await sendPrompt(newMessage, elem);
                          }
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="new-prompt-input-container">
          {waitingForResponse ? (
            <Spinner />
          ) : (
            <input
              type="text"
              className="input-field"
              placeholder="Start a new chat ..."
              onKeyDown={async e => {
                if (e.key === "Enter") {
                  const newMessage = (e.target as HTMLInputElement).value;
                  (e.target as HTMLInputElement).value = "";
                  await sendPrompt(newMessage, {});
                }
              }}
            />
          )}
        </div>
      </div>
      {showSqlModal && (
        <Modal show onHide={() => setShowSqlModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>SQL</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>{sqlText}</p>
          </Modal.Body>
        </Modal>
      )}
      {showReasonModal && (
        <Modal show onHide={() => setShowReasonModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>LLM Reasoning</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>{reasonText}</p>
          </Modal.Body>
        </Modal>
      )}
    </Page>
  );
};

export default LLM;
